"use client";

import { type ColumnVisibilityState, flexRender, type RowData, useTable } from "@tanstack/react-table";
import React from "react";
import * as R from "remeda";
import { cn } from "../../tailwind/cn";
import { isOwnDomEvent } from "../../utils/isOwnDomEvent";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Table";
import { DataTableColumnHeader } from "./ColumnHeader";
import { type ColumnDef, dataTableFeatures } from "./features";
import { DataTablePagination } from "./Pagination";
import { DataTableToolbar } from "./Toolbar";
import type {
  ColumnFiltersProps,
  ExpandedProps,
  GlobalFilterProps,
  PaginationProps,
  RowSelectionProps,
  SortingProps,
} from "./types";
import { assertUniqueColumnIds, getColumnId, useFilters, useSorting } from "./utils";

export type { ColumnFiltersProps, ExpandedProps, PaginationProps, RowSelectionProps, SortingProps } from "./types";
export { createColumnHelper } from "./features";
export type { CellContext, ColumnDef } from "./features";

export type DataTableProps<TData extends RowData> = {
  className?: string;

  data: Array<TData>;
  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  columns: Array<ColumnDef<TData, any>>;
  initialColumnVisibility?: ColumnVisibilityState;

  DetailsComponent?: React.ComponentType<{ data: TData }>;

  loading?: boolean;
  hideToolbar?: boolean;
  headerClassName?: string;
} & PaginationProps &
  SortingProps &
  GlobalFilterProps &
  ColumnFiltersProps &
  RowSelectionProps &
  ExpandedProps;

export function DataTable<TData extends RowData & { id: number | string }>({
  className,
  columns,
  initialColumnVisibility,
  data,
  loading,
  hideToolbar,
  headerClassName,
  rowCount,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  globalFilter,
  globalFilterLabel,
  onGlobalFilterChange,
  enableMultiRowSelection,
  rowSelection = {},
  onRowSelectionChange,
  expanded,
  onExpandedChange,
  DetailsComponent,
}: Readonly<DataTableProps<TData>>) {
  // unconditional: `useFilters` only validates defs when the table is filterable, but a duplicate id breaks
  // any table, so this has to run for every one of them.
  assertUniqueColumnIds(columns);

  const filterProps = useFilters({ columns, columnFilters, onColumnFiltersChange });
  const sortingProps = useSorting({ columns, sorting, onSortingChange });

  const fromColumns = R.mapToObj(columns, (column) => [getColumnId(column), !column.meta?.defaultHidden]);
  const columnVisibility = initialColumnVisibility ? { ...fromColumns, ...initialColumnVisibility } : fromColumns;

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns,
    manualPagination: true,
    rowCount,
    onPaginationChange,
    manualSorting: true,
    enableSorting: !!onSortingChange,
    onSortingChange: sortingProps.onSortingChange,
    manualFiltering: true,
    enableColumnFilters: !!onColumnFiltersChange,
    onColumnFiltersChange: filterProps.onColumnFiltersChange,
    onGlobalFilterChange,
    renderFallbackValue: "N/A",
    enableMultiRowSelection,
    getRowId: (row) => `${row.id}`,
    onRowSelectionChange,
    manualExpanding: !!DetailsComponent,
    getRowCanExpand: () => !!DetailsComponent,
    onExpandedChange,
    state: {
      pagination,
      sorting: sortingProps.sorting,
      columnFilters: filterProps.columnFilters,
      globalFilter,
      rowSelection,
      expanded,
    },
    initialState: { columnVisibility },
  });

  return (
    <div className={cn("relative flex h-full min-h-0 flex-col rounded-sm border border-gray bg-white", className)}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
          <div className="loading-spinner size-7 text-brand" />
        </div>
      )}
      {!hideToolbar && <DataTableToolbar table={table} globalFilterLabel={globalFilterLabel} />}
      <Table className="text-small">
        <TableHeader className={cn("sticky top-0 z-10 bg-white shadow-down", headerClassName)}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                if (header.column.columnDef.meta?.filter?.hideColumn) return null;
                const headerProps = header.getContext();
                const isFirstInGroup =
                  table.getHeaderGroups().length > 1 &&
                  (!header.column.parent || header.column.id === header.column.parent.columns[0].id);
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      "border-b-0 border-medium-gray px-1.5",
                      hideToolbar && headerGroup.depth === 0 && "border-t",
                      isFirstInGroup && "border-l",
                    )}
                  >
                    {!header.isPlaceholder && (
                      <div className="flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, headerProps)}
                        <DataTableColumnHeader {...headerProps} />
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className={cn(pagination && "[&_tr:last-child_td]:border-b-0")}>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() && "selected"}
                  onClick={(event) => {
                    if (isOwnDomEvent(event)) row.toggleSelected();
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    if (cell.column.columnDef.meta?.filter?.hideColumn) return null;
                    const isFirstInGroup =
                      table.getHeaderGroups().length > 1 &&
                      (!cell.column.parent || cell.column.id === cell.column.parent.columns[0].id);
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "border-medium-gray p-1.5",
                          isFirstInGroup && "border-l",
                          cell.row.index % 2 !== 0 && "bg-light-gray/20",
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {row.getIsExpanded() && !!DetailsComponent && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <DetailsComponent data={row.original} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-20 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!!pagination && <DataTablePagination table={table} />}
    </div>
  );
}

export { filterColumnHelper } from "./utils";
